import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { can } from "../lib/roles";
import { PageHeader, Panel } from "../components/Surface";
import { Button } from "../components/ui/Button";
import { inputClass } from "../components/FormKit";
import { labelize } from "../lib/format";
import {
  securityDashboard, securityAccount, suspendAccount, terminateAccount, reinstateAccount,
  ABUSE_BAND_TONE, type Dashboard, type AbusiveAccount, type AccountDetail,
} from "../lib/security";

const STATUS_TONE: Record<string, string> = {
  active: "text-verified", suspended: "text-[#c9a227]", terminated: "text-danger",
};

export function AdminSecurity() {
  const { roles } = useAuth();
  const isSec = can(roles, "admin_security");
  const [data, setData] = useState<Dashboard | null>(null);
  const [selected, setSelected] = useState<AccountDetail | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  async function refresh() {
    if (!isSec) return;
    setData(await securityDashboard());
  }
  useEffect(() => { void refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [isSec]);

  async function open(a: AbusiveAccount) {
    setReason("");
    setSelected(await securityAccount(a.id));
  }

  async function act(kind: "suspend" | "terminate" | "reinstate", id: string) {
    setBusy(true);
    try {
      if (kind === "suspend") await suspendAccount(id, reason);
      else if (kind === "terminate") await terminateAccount(id, reason);
      else await reinstateAccount(id);
      await refresh();
      setSelected(await securityAccount(id));
    } finally { setBusy(false); }
  }

  if (!isSec) return <p className="mx-auto max-w-3xl font-mono text-sm text-danger">You don't have access to the security console.</p>;

  return (
    <div className="mx-auto grid w-full max-w-5xl gap-6">
      <PageHeader
        title="Attack dashboard"
        subtitle="Abuse signals across the platform — blocked uploads, suspended-access strikes, reports, and risk. Suspend or terminate abusive accounts; suspension is enforced on every request."
        actions={data && (
          <span className="font-mono text-[11px] text-muted" data-testid="suspended-count">{data.suspended_count} suspended/terminated</span>
        )}
      />

      {!data ? (
        <p className="font-mono text-sm text-muted">Loading…</p>
      ) : (
        <>
          {/* Event-kind summary cards */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="event-counts">
            {Object.keys(data.event_counts).length === 0 ? (
              <p className="col-span-full font-mono text-sm text-muted">No security events in the last 30 days.</p>
            ) : Object.entries(data.event_counts).map(([kind, n]) => (
              <div key={kind} className="grid gap-1 rounded-lg border border-line p-3 text-center">
                <span className="text-2xl font-extrabold tabular-nums text-danger">{n}</span>
                <span className="font-mono text-[10px] uppercase text-muted">{labelize(kind)}</span>
              </div>
            ))}
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {/* Ranked abusive accounts */}
            <Panel title={`Abusive accounts (${data.top_accounts.length})`}>
              {data.top_accounts.length === 0 ? (
                <p className="font-mono text-sm text-muted">No flagged accounts. All clear.</p>
              ) : (
                <ul className="grid gap-1.5" data-testid="abusive-accounts">
                  {data.top_accounts.map((a) => (
                    <li key={a.id}>
                      <button type="button" onClick={() => open(a)}
                        className={`flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left text-sm hover:bg-surface-2 ${selected?.account.id === a.id ? "border-accent" : "border-line"}`}>
                        <span className="grid gap-0.5">
                          <span className="font-mono">{a.email ?? a.id.slice(0, 8)}</span>
                          <span className="font-mono text-[10px] text-muted">
                            {a.security_events} events · {a.upheld_reports} upheld · risk {a.risk_band ?? "—"}
                          </span>
                        </span>
                        <span className="flex items-center gap-2">
                          <span className={`font-mono text-[10px] font-bold uppercase ${STATUS_TONE[a.status] ?? "text-muted"}`}>{a.status}</span>
                          <span className={`font-mono text-sm font-bold tabular-nums ${ABUSE_BAND_TONE[a.band]}`}>{a.score}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            {/* Selected-account detail + actions */}
            <Panel title="Account detail">
              {!selected ? (
                <p className="font-mono text-sm text-muted">Select an account to review its activity and take action.</p>
              ) : (
                <div className="grid gap-3" data-testid="account-detail">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-mono text-sm">{selected.account.email ?? selected.account.id.slice(0, 8)}</span>
                    <span className={`font-mono text-[11px] font-bold uppercase ${STATUS_TONE[selected.account.status] ?? "text-muted"}`} data-testid="detail-status">{selected.account.status}</span>
                  </div>
                  <p className="font-mono text-[11px] text-muted">
                    abuse score <b className={ABUSE_BAND_TONE[selected.band]}>{selected.score}</b> ({selected.band}) ·
                    {" "}{selected.signals.security_events} events · {selected.signals.open_reports} open / {selected.signals.upheld_reports} upheld reports · risk {selected.signals.risk_band ?? "—"}
                  </p>
                  {selected.account.suspended_reason && (
                    <p className="font-mono text-[11px] text-muted">Reason on file: {selected.account.suspended_reason}</p>
                  )}

                  {selected.security_events.length > 0 && (
                    <ul className="max-h-40 overflow-y-auto rounded border border-line p-2" data-testid="detail-events">
                      {selected.security_events.slice(0, 20).map((e) => (
                        <li key={e.id} className="font-mono text-[10px] text-muted">
                          {new Date(e.created_at).toLocaleString()} · {labelize(e.kind)}{e.ip ? ` · ${e.ip}` : ""}
                        </li>
                      ))}
                    </ul>
                  )}

                  {selected.account.status === "active" ? (
                    <>
                      <textarea className={inputClass} rows={2} placeholder="Reason (recorded on the account + audit log)" value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Action reason" />
                      <div className="flex gap-2">
                        <Button disabled={busy} onClick={() => act("suspend", selected.account.id)} data-testid="suspend-btn">Suspend</Button>
                        <Button variant="ghost" disabled={busy} onClick={() => act("terminate", selected.account.id)} data-testid="terminate-btn">Terminate</Button>
                      </div>
                    </>
                  ) : (
                    <Button disabled={busy} onClick={() => act("reinstate", selected.account.id)} data-testid="reinstate-btn">Reinstate account</Button>
                  )}
                </div>
              )}
            </Panel>
          </div>

          {/* Rate-limit hotspots */}
          {data.rate_hotspots.length > 0 && (
            <Panel title="Rate-limit hotspots (last hour)">
              <ul className="grid gap-1" data-testid="rate-hotspots">
                {data.rate_hotspots.map((h) => (
                  <li key={h.key} className="flex justify-between font-mono text-[11px]">
                    <span className="text-muted">{h.key}</span><span className="font-bold text-danger">{h.count}</span>
                  </li>
                ))}
              </ul>
            </Panel>
          )}

          {/* Recent events feed */}
          <Panel title="Recent security events">
            {data.recent_events.length === 0 ? (
              <p className="font-mono text-sm text-muted">No events yet.</p>
            ) : (
              <ul className="grid gap-1" data-testid="recent-events">
                {data.recent_events.map((e) => (
                  <li key={e.id} className="flex items-center justify-between gap-3 font-mono text-[11px]">
                    <span className={e.severity >= 2 ? "text-danger" : "text-muted"}>{labelize(e.kind)}</span>
                    <span className="text-muted">{e.ip ?? "—"} · {new Date(e.created_at).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
